import { dataService } from '../apis/dataService';
import type {
  BalanceSheet,
  CashflowStatement,
  ClientOverview,
  Currency,
  Unit,
} from '../../types/api';
import { convertUSDToCurrency } from './currencyConversion';
import {
  convertUnitValueToTargetCurrency,
  fetchStatementsForUnits,
  findLatestReportedDate,
  groupStatementRowsByUnit,
  latestStatementDateKey,
  resolvePeriodWindow,
  sumCategoryValueAtWindowEnd,
  sumCategoryValueInWindow,
  type PeriodMode,
  type PeriodWindow,
} from './unitFinancials';

const EQUITY_VALUATION_CODE = 13;
const UNEARNED_REVENUE_CODE = 11;
const CASH_IN_FROM_OPERATION_CODE = 2;

export interface PortfolioExtendedKpiMetrics {
  netEquity: number;
  ttmCashReceived: number;
  portfolioCoc: number;
  portfolioNoiYield: number;
  capitalAtRisk: number;
  capitalAtRiskPropertyCount: number;
}

export interface PortfolioStatementSnapshot {
  balanceSheetData: Array<BalanceSheet & { unit_id_no: number }>;
  cashflowData: Array<CashflowStatement & { unit_id_no: number }>;
}

function sumBalanceSheetCategoryAtWindowEnd(
  balanceSheetData: Array<BalanceSheet & { unit_id_no: number }>,
  categoryCode: number,
  periodWindow: PeriodWindow,
  allUnits: Unit[],
  targetCurrency: string,
  currencies: Currency[]
): number {
  const rowsByUnit = groupStatementRowsByUnit(balanceSheetData);
  let total = 0;

  rowsByUnit.forEach((unitRows, unitId) => {
    const latestTotal = sumCategoryValueAtWindowEnd(unitRows, categoryCode, periodWindow);

    if (latestTotal !== 0) {
      total += convertUnitValueToTargetCurrency(
        latestTotal,
        unitId,
        allUnits,
        targetCurrency,
        currencies
      );
    }
  });

  return total;
}

function countUnitsWithBalanceSheetCategoryAtWindowEnd(
  balanceSheetData: Array<BalanceSheet & { unit_id_no: number }>,
  categoryCode: number,
  periodWindow: PeriodWindow
): number {
  const rowsByUnit = groupStatementRowsByUnit(balanceSheetData);
  let count = 0;

  rowsByUnit.forEach((unitRows) => {
    if (sumCategoryValueAtWindowEnd(unitRows, categoryCode, periodWindow) > 0) {
      count += 1;
    }
  });

  return count;
}

function sumCashReceivedInWindow(
  cashflowData: Array<CashflowStatement & { unit_id_no: number }>,
  periodWindow: PeriodWindow,
  allUnits: Unit[],
  targetCurrency: string,
  currencies: Currency[]
): number {
  const rowsByUnit = groupStatementRowsByUnit(cashflowData);
  let total = 0;

  rowsByUnit.forEach((unitRows, unitId) => {
    const windowTotal = sumCategoryValueInWindow(
      unitRows,
      CASH_IN_FROM_OPERATION_CODE,
      periodWindow
    );

    if (windowTotal !== 0) {
      total += convertUnitValueToTargetCurrency(
        windowTotal,
        unitId,
        allUnits,
        targetCurrency,
        currencies
      );
    }
  });

  return total;
}

/** Latest reported month shared by the portfolio KPIs, independent of the active chart. */
export function portfolioStatementAnchor(snapshot: PortfolioStatementSnapshot): string | null {
  return latestStatementDateKey([
    findLatestReportedDate(snapshot.cashflowData, [CASH_IN_FROM_OPERATION_CODE]),
    findLatestReportedDate(snapshot.balanceSheetData, [EQUITY_VALUATION_CODE, UNEARNED_REVENUE_CODE]),
  ]);
}

function portfolioPeriodWindow(snapshot: PortfolioStatementSnapshot, periodMode: PeriodMode): PeriodWindow {
  return resolvePeriodWindow(periodMode, portfolioStatementAnchor(snapshot));
}

const emptyExtendedKpis: PortfolioExtendedKpiMetrics = {
  netEquity: 0,
  ttmCashReceived: 0,
  portfolioCoc: 0,
  portfolioNoiYield: 0,
  capitalAtRisk: 0,
  capitalAtRiskPropertyCount: 0,
};

export async function loadPortfolioStatements(
  userId: number,
  clientUnits: Array<{ unit_id_no?: number }>
): Promise<PortfolioStatementSnapshot> {
  if (!clientUnits.length) {
    return { balanceSheetData: [], cashflowData: [] };
  }

  const [balanceSheetData, cashflowData] = await Promise.all([
    fetchStatementsForUnits(userId, clientUnits, dataService.getBalanceSheet),
    fetchStatementsForUnits(userId, clientUnits, dataService.getCashflow),
  ]);

  return { balanceSheetData, cashflowData };
}

export function calculatePortfolioExtendedKpiMetrics(
  snapshot: PortfolioStatementSnapshot,
  overviewData: ClientOverview[],
  allUnits: Unit[],
  targetCurrency: string,
  currencies: Currency[],
  periodMode: PeriodMode = 'ttm'
): PortfolioExtendedKpiMetrics {
  if (!snapshot.balanceSheetData.length && !snapshot.cashflowData.length) {
    return emptyExtendedKpis;
  }

  const periodWindow = portfolioPeriodWindow(snapshot, periodMode);

  const netEquity = sumBalanceSheetCategoryAtWindowEnd(
    snapshot.balanceSheetData,
    EQUITY_VALUATION_CODE,
    periodWindow,
    allUnits,
    targetCurrency,
    currencies
  );

  const ttmCashReceived = sumCashReceivedInWindow(
    snapshot.cashflowData,
    periodWindow,
    allUnits,
    targetCurrency,
    currencies
  );

  const capitalAtRisk = sumBalanceSheetCategoryAtWindowEnd(
    snapshot.balanceSheetData,
    UNEARNED_REVENUE_CODE,
    periodWindow,
    allUnits,
    targetCurrency,
    currencies
  );

  const capitalAtRiskPropertyCount = countUnitsWithBalanceSheetCategoryAtWindowEnd(
    snapshot.balanceSheetData,
    UNEARNED_REVENUE_CODE,
    periodWindow
  );

  const totalPortfolioValueUsd = overviewData.reduce(
    (total, overview) => total + (overview.total_assets_usd || 0),
    0
  );

  const totalNoiUsd = overviewData.reduce(
    (total, overview) => total + (overview.net_annual_rent_yield_usd || 0),
    0
  );

  const totalPortfolioValue = convertUSDToCurrency(
    totalPortfolioValueUsd,
    targetCurrency,
    currencies
  );

  const totalNoi = convertUSDToCurrency(totalNoiUsd, targetCurrency, currencies);

  const portfolioCoc = netEquity > 0 ? (ttmCashReceived / netEquity) * 100 : 0;
  const portfolioNoiYield =
    totalPortfolioValue > 0 ? (totalNoi / totalPortfolioValue) * 100 : 0;

  return {
    netEquity,
    ttmCashReceived,
    portfolioCoc,
    portfolioNoiYield,
    capitalAtRisk,
    capitalAtRiskPropertyCount,
  };
}

export async function fetchPortfolioExtendedKpiMetrics(
  userId: number,
  clientUnits: Array<{ unit_id_no?: number }>,
  overviewData: ClientOverview[],
  allUnits: Unit[],
  targetCurrency: string,
  currencies: Currency[],
  periodMode: PeriodMode = 'ttm'
): Promise<PortfolioExtendedKpiMetrics> {
  const snapshot = await loadPortfolioStatements(userId, clientUnits);
  return calculatePortfolioExtendedKpiMetrics(
    snapshot,
    overviewData,
    allUnits,
    targetCurrency,
    currencies,
    periodMode
  );
}

export function formatCompactCurrency(value: number, currencyCode: string): string {
  const abs = Math.abs(value);

  if (abs >= 1_000_000) {
    const scaled = value / 1_000_000;
    const text = scaled.toFixed(1).replace(/\.0$/, '');
    return `${currencyCode} ${text}M`;
  }

  if (abs >= 1_000) {
    const scaled = value / 1_000;
    const text = scaled.toFixed(0);
    return `${currencyCode} ${text}K`;
  }

  return `${currencyCode} ${value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

export function formatPercentValue(value: number, digits = 1): string {
  return `${value.toFixed(digits).replace(/\.0$/, '')}%`;
}
